/**
 * Área 4 — Valores complementares e resumo: frete, outras despesas e o resumo do pedido, que
 * acompanha a digitação (mesma fórmula do banco). Também as parcelas, quando a forma de
 * pagamento gera contas a receber.
 */
import {
  buildInstallments,
  formatMoney,
  parseDecimalBR,
  sumMoney,
  toFormMoney,
  type PaymentMethod,
} from "@cafegestor/shared";
import { useState } from "react";
import { Button, Field, Input } from "@/components/ui";
import { DecimalInput } from "@/components/DecimalInput";
import type { InstallmentRow } from "./draft";

type Preview = {
  products: string | null;
  discounts: string;
  freight: string;
  other: string;
  total: string | null;
};

export function SaleSummary({
  freight,
  other,
  preview,
  onFreight,
  onOther,
  disabled,
}: {
  freight: string;
  other: string;
  preview: Preview;
  onFreight: (v: string) => void;
  onOther: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-wrap items-start gap-3">
        <Field label="Frete (R$)" hint="Rateado pelo valor dos itens">
          <DecimalInput
            className="w-36"
            value={freight}
            {...(disabled !== undefined ? { disabled } : {})}
            onChange={(ev) => onFreight(ev.target.value)}
          />
        </Field>
        <Field label="Outras despesas (R$)" hint="Também rateadas pelos itens">
          <DecimalInput
            className="w-36"
            value={other}
            {...(disabled !== undefined ? { disabled } : {})}
            onChange={(ev) => onOther(ev.target.value)}
          />
        </Field>
      </div>
      <dl
        aria-label="Resumo do pedido"
        className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-md bg-muted px-4 py-3 text-sm"
      >
        <dt>Subtotal dos produtos (bruto)</dt>
        <dd className="text-right">
          {preview.products === null ? "—" : formatMoney(preview.products)}
        </dd>
        <dt>Descontos</dt>
        <dd className="text-right">
          {Number(preview.discounts) === 0 ? "—" : `− ${formatMoney(preview.discounts)}`}
        </dd>
        <dt>Frete</dt>
        <dd className="text-right">{formatMoney(preview.freight)}</dd>
        <dt>Outras despesas</dt>
        <dd className="text-right">{formatMoney(preview.other)}</dd>
        <dt className="border-t pt-1 font-semibold">Total geral</dt>
        <dd className="border-t pt-1 text-right font-semibold">
          {preview.total === null ? "— (informe os preços)" : formatMoney(preview.total)}
        </dd>
      </dl>
    </div>
  );
}

/** Parcelas a receber (forma de pagamento a prazo). */
export function InstallmentsFields({
  rows,
  onChange,
  total,
  orderDate,
  method,
  disabled,
}: {
  rows: InstallmentRow[];
  onChange: (rows: InstallmentRow[]) => void;
  total: string | null;
  orderDate: string;
  method: PaymentMethod | undefined;
  disabled?: boolean;
}) {
  const [countInput, setCount] = useState(rows.length ? String(rows.length) : "");
  const [error, setError] = useState<string | null>(null);
  const count = countInput || String(Math.max(1, method?.installments || 1));
  const amounts = rows.map((r) => parseDecimalBR(r.amount, "dinheiro"));
  const sum = amounts.every((a) => a !== null) ? sumMoney(amounts as string[]) : null;

  function generate() {
    if (!orderDate) return setError("Informe a data do pedido para gerar as parcelas.");
    if (total === null || Number(total) <= 0)
      return setError("Inclua os produtos (com preço) antes de gerar as parcelas.");
    const n = Math.min(120, Math.max(1, Number.parseInt(count, 10) || 1));
    try {
      const drafts = buildInstallments({
        total,
        count: n,
        baseDate: orderDate,
        firstDueDays: method?.firstDueDays ?? 30,
        intervalDays: method?.intervalDays ?? 30,
      });
      setError(null);
      onChange(
        drafts.map((d) => ({ key: d.number, dueDate: d.dueDate, amount: toFormMoney(d.amount) })),
      );
    } catch {
      setError("Não foi possível gerar as parcelas: confira a data e o total.");
    }
  }

  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <span className="font-medium">Parcelas a receber</span>
        <div className="flex items-end gap-2">
          <Field label="Parcelas">
            <Input
              className="w-20"
              inputMode="numeric"
              value={count}
              disabled={disabled}
              onChange={(ev) => setCount(ev.target.value)}
            />
          </Field>
          <Button size="sm" variant="secondary" disabled={disabled} onClick={generate}>
            Gerar{" "}
            {method ? `(${method.firstDueDays}/${method.intervalDays} dias)` : "(30 em 30 dias)"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() =>
              onChange([
                ...rows,
                { key: Math.max(0, ...rows.map((r) => r.key)) + 1, dueDate: orderDate, amount: "" },
              ])
            }
          >
            Adicionar parcela
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key} className="grid gap-2 sm:grid-cols-12 sm:items-center">
            <span className="text-muted-foreground sm:col-span-1">{i + 1}ª</span>
            <div className="sm:col-span-4">
              <Input
                type="date"
                aria-label={`Vencimento da ${i + 1}ª parcela`}
                value={r.dueDate}
                disabled={disabled}
                onChange={(ev) =>
                  onChange(
                    rows.map((x) => (x.key === r.key ? { ...x, dueDate: ev.target.value } : x)),
                  )
                }
              />
            </div>
            <div className="sm:col-span-4">
              <DecimalInput
                aria-label={`Valor da ${i + 1}ª parcela`}
                value={r.amount}
                {...(disabled !== undefined ? { disabled } : {})}
                onChange={(ev) =>
                  onChange(
                    rows.map((x) => (x.key === r.key ? { ...x, amount: ev.target.value } : x)),
                  )
                }
              />
            </div>
            <div className="text-right sm:col-span-3">
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
              >
                Remover
              </Button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-muted-foreground">
        Soma das parcelas: <strong>{sum === null ? "—" : formatMoney(sum)}</strong> · total do
        pedido: <strong>{total === null ? "—" : formatMoney(total)}</strong>
      </p>
      {error && <p className="mt-1 text-destructive">{error}</p>}
    </div>
  );
}
